import { getCacheListByKey, getObjectByCache } from '@/utils/basicDataCache'
import { TYPE_NAMES } from '@/utils/class/AdvanceSelect'
import { ChargeUtils } from '@/class/cargo/model/utils/ChargeUtils'
import { Company } from '@/class/model/mdm/Company'
import * as RateUtil from '@/class/cargo/model/utils/RateUtil'
import * as NumberUtils from '@/utils/NumberUtils'
import { getSettlementAgentsByCmpIdx } from '@/api/company'
import { Job } from '@/class/model/booking/Job'
import { AirExportJob } from '@/class/model/booking/AirExportJob'
import { AirImportJob } from '@/class/model/booking/AirImportJob'
import { SeaExportJob } from '@/class/model/booking/SeaExportJob'
import { SeaImportJob } from '@/class/model/booking/SeaImportJob'
import { RailwayExportJob } from '@/class/model/booking/RailwayExportJob'
import { cache, cacheName } from '@/utils/cacheManager'
import { ChargeItem } from '@/class/model/mdm/chargeItem'
import { multiply } from '@/utils/commonUtil'
import { getContacts } from '@/api/partner/contact'
import * as ChargeMeasure from '@/class/cargo/model/parameters/ChargeMeasure'
import ColumnUtils from '@/utils/ColumnUtils'
import * as BillType from '@/class/cargo/model/parameters/BillType'
import * as CargoDocConsts from '@/class/cargo/model/parameters/CargoDocConsts'
import { basicCacheKeys } from '@/utils/cacheConfig'

/**
 * 费用列表联动公共方法、改动请注意【使用范围】
 * 使用范围：费用页签、申请新增费用、申请修改费用、结算模块-更改费用审核-【全部】页签复制新增，修改
 */
export class ChargeColumnUtil {
  /**
   * 费用项选择
   * @param cost 费用对象
   * @param params 表格参数
   * @param isRowEdit 该行可编辑属性、用于费用页签判断，其他地方使用为true即可
   * @param job 业务
   * @param currentViewCacheData 缓存对象
   * @param taxRateModifyMode 税率修改方式（0：默认 1：录入费用名称，若费用名称税率不为0的，更新此条费用的税率，否则不更新）
   * @param isUpdateUnitRate CARGO-30978 环海：更改费用名称，不要清空价格
   * @param accountObjectControlMode 结算单位控制模式
   * @returns {Promise<void>}
   */
  static async chargeItemChange(cost, params, isRowEdit = false, job, currentViewCacheData, taxRateModifyMode = 0, isUpdateUnitRate = false, accountObjectControlMode = false, isZY = false, isExcludedOffice = false) {
    const chargeItem = getObjectByCache(TYPE_NAMES.basic_data_ChargeItem, 'itemId', params.value)
    let isSame = true // 新的收费项目的默认结算信息和现在结算信息是否一致
    if (chargeItem && cost) {
      if (taxRateModifyMode === 1) {
        // CARGO-31015 录入或修改费用名称，不论费用名称在收费项目维护的税率是否为0，都以费用名称的税率更新此条费用的税率，0税率的税率也为0（以费用名称税率为准）
        cost.taxRate = chargeItem.taxRate || 0
      }
      if (isZY && cost.receivable && !isExcludedOffice) {
        // CARGO-43194 更改可售卖产品-全程/国内各个场景下的费用匹配逻辑
        const taxRate = ColumnUtils.getTaxRateFromMapping(params.data.chargeItemId, job.saleProductCode, job.fullOrInside)
        cost.taxRate = taxRate
      }
      if (params.originalValue && Number(params.originalValue) > 0) {
        // 当费用项目没有维护默认的币种，单价，单位，结算对象时， 此时只修改修改费用名称
        if (cost.receivable) {
          if (!chargeItem.defaultarcurrency && !chargeItem.defaultarprice && !chargeItem.serviceType && !chargeItem.defaultArAccountId && !chargeItem.taxRate) {
            cost.chargeItemId = chargeItem.itemId
          } else {
            if (chargeItem.defaultarcurrency && chargeItem.defaultarcurrency !== cost.currency) { // 比较币种
              isSame = false
            }
            if (chargeItem.defaultarprice > 0 && chargeItem.defaultarprice !== cost.unitRate) { // 比较单价
              isSame = false
            }
            if (chargeItem.defaultArAccountId > 0 && chargeItem.defaultArAccountId !== cost.accountId) { // 比较结算对象
              isSame = false
            }
            if (chargeItem.serviceType && chargeItem.serviceType !== cost.serviceType) { // 比较服务项目
              isSame = false
            }
          }
        } else {
          if (!chargeItem.defaultapcurrency && !chargeItem.defaultapprice && !chargeItem.serviceType && !chargeItem.defaultApAccountId && !chargeItem.taxRate) {
            cost.chargeItemId = chargeItem.itemId
          } else {
            if (chargeItem.defaultapcurrency && chargeItem.defaultapcurrency !== cost.currency) { // 比较币种
              isSame = false
            }
            if (chargeItem.defaultapprice > 0 && chargeItem.defaultapprice !== cost.unitRate) { // 比较单价
              isSame = false
            }
            if (chargeItem.defaultApAccountId > 0 && chargeItem.defaultApAccountId !== cost.accountId) { // 比较结算对象
              isSame = false
            }
            if (chargeItem.serviceType && chargeItem.serviceType !== cost.serviceType) { // 比较服务项目
              isSame = false
            }
          }
        }
        if (chargeItem.taxRate > 0 && chargeItem.taxRate !== cost.taxRate) { // 税率
          isSame = false
        }
      } else { // 新增默认都是带出所有默认项目
        isSame = false
      }
      if (!isSame) {
        if (taxRateModifyMode !== 1) {
          // CARGO-31015 录入或修改费用名称，不论费用名称在收费项目维护的税率是否为0，都以费用名称的税率更新此条费用的税率，0税率的税率也为0（以费用名称税率为准）
          cost.taxRate = chargeItem.taxRate || 0
        }
        if (cost.receivable) {
          await this.changeArCl(chargeItem, cost, job, currentViewCacheData, taxRateModifyMode, accountObjectControlMode)
        } else {
          await this.changeApCl(chargeItem, cost, job, currentViewCacheData)
        }
        cost.serviceType = chargeItem.serviceType
      } else { // CARGO-36490 14475天津中船：新增费用无法更新单位信息
        if (chargeItem.measure) {
          cost.measure = chargeItem.measure
        }
      }
      // CARGO-30978 环海：更改费用名称，不要清空价格
      let unitRate = cost.receivable ? chargeItem.defaultarprice : chargeItem.defaultapprice
      if (!unitRate) unitRate = 0
      cost.unitRate = (isUpdateUnitRate && unitRate === 0) ? cost.unitRate : unitRate
      // CARGO-31003 在新增费用时，直接对特定的费用进行受控
      if (cost.uuid.startsWith('addTabSelling__') || cost.uuid.startsWith('addTabCost__')) {
        cost.controlled = chargeItem.controlled
      } else if (!cost.controlled) {
        cost.controlled = chargeItem.controlled
      }
      cost.needInvoice = chargeItem.needInvoice
    }
    if (isRowEdit) {
      if (chargeItem && params.originalValue !== params.value) {
        cost.quantity = await this.getNowQuantity(cost, job)
        // CARGO-4980 单价是否含税
        if (cost.unitPriceIncludeTax) {
          ChargeUtils.calculateTaxAmount(cost, multiply(cost.quantity, cost.unitRate), cost.taxRate)
        } else {
          ChargeUtils.calculateTax(cost, multiply(cost.quantity, cost.unitRate), cost.taxRate)
        }
      }
    }
  }

  /**
   * 获取当前件数
   * @param cost
   * @param job
   * @returns {Promise<*>}
   */
  static async getNowQuantity(cost, job) {
    await cache.load(cacheName.CONFIG)
    const cacheConfig = cache.config
    const measureData = this.queryMeasureData(cost.measure)
    if (measureData.specifyBoxType && measureData.eqSize) {
      cost.quantity = await this.getSpecifyBoxTypeQuantity(measureData.eqSize, measureData.eqType, job)
    } else {
      if (cost.measure === ChargeMeasure.WGT_CHARGE_MEASURE.code) {
        const weight = ChargeUtils.getChargeWeightValueType(true) === 0 ? job.weightActual : job.weightContract
        if (job instanceof AirImportJob) {
          cost.quantity = job.weightContract
        } else {
          cost.quantity = weight
        }
      } else if (cost.measure === ChargeMeasure.WGTT_CHARGE_MEASURE.code) {
        if (job instanceof SeaExportJob && BillType.MERGE_BILL_TYPE.code === job.typeHBL) {
          const hbl2 = job.seaCargoDoc.find(p => p.docType === CargoDocConsts.HBL2)
          if (hbl2 && hbl2.weight) {
            cost.quantity = hbl2.weight / 1000
          }
        } else if (job instanceof AirImportJob) {
          cost.quantity = job.weightContract / 1000
        } else {
          const weight = ChargeUtils.getChargeWeightValueType(true) === 0 ? job.weightActual : job.weightContract
          cost.quantity = weight / 1000
        }
      } else if (cost.measure === 'WGTX') {
        if (job instanceof AirExportJob) {
          const getHBLcWeight = parseInt(cacheConfig.get('GetHBLcWeight')) === 1
          if (getHBLcWeight) {
            cost.quantity = await ChargeUtils.getMHLcWeight(cost.receivable, job)
          } else {
            cost.quantity = new AirExportJob(job).cWeight
          }
        } else {
          cost.quantity = job.chargeWeight
        }
      } else if (cost.measure === 'PIECE') {
        if (job instanceof AirExportJob) {
          cost.quantity = (job).quantityActual
        } else if (job instanceof AirImportJob) {
          cost.quantity = (job).quantityContract
        } else if (job instanceof SeaExportJob) {
          cost.quantity = (job).quantityActual
        } else if (job instanceof SeaImportJob) {
          cost.quantity = (job).quantityContract
        } else if (job instanceof RailwayExportJob) {
          cost.quantity = (job).quantityActual
        }
      } else if (cost.measure === 'VOL') {
        cost.quantity = ChargeUtils.getVOLQuantity(job)
      } else if (cost.measure === 'MIN') {
        cost.quantity = 1
      } else if (cost.measure === 'NML') {
        cost.quantity = 1
      } else if (cost.measure === 'BOX') {
        cost.quantity = 1
      } else if (cost.measure === 'CNTR') {
        cost.quantity = 0
        if (!job || !job.seaEQ) {
          await new Job(job).getSeaEQ()
        }
        if (job.seaEQ) {
          for (const seq of job.seaEQ) {
            cost.quantity += seq.eqQuantity
          }
        }
      } else if (cost.measure === 'BL') {
        cost.quantity = 1
      } else if (cost.measure === 'SHIPMENT') {
        cost.quantity = 1
      } else {
        cost.quantity = await this.getEQQuantity(cost.measure, job)
      }
    }
    if (cost.quantity < 1) {
      cost.quantity = 1
    }
    cost.quantity = await ChargeUtils.getQuantityByChargeWeightValueType2(job, cost.quantity, cost.measure)
    return cost.quantity
  }

  //
  //   /**
  //    * 统计箱量
  //    */
  static async getEQQuantity(type, job) {
    let n = 0
    if (!job || !job.seaEQ) {
      await new Job(job).getSeaEQ()
    }
    if (!job.seaEQ) {
      return n
    }
    for (const eq of job.seaEQ) {
      if (type === 'C20' && eq.eqSize === '20') {
        n = n + eq.eqQuantity
      } else if (type === 'C40' && eq.eqSize === '40' && eq.eqType === 'GP') {
        n = n + eq.eqQuantity
      } else if (type === 'C40H' && eq.eqSize === '40' && eq.eqType === 'HC') {
        n = n + eq.eqQuantity
      } else if (type === 'C45' && eq.eqSize === '45') {
        n = n + eq.eqQuantity
      }
    }
    return n
  }

  /**
   * 费用项选择(应收)
   */
  static async changeArCl(c, charge, job, currentViewCacheData, taxRateModifyMode = 0, accountObjectControlMode = false) {
    if (c) {
      const oldAccountId = charge.accountId
      const localCurrency = RateUtil.localCurrencyByOfficeId(charge.branchId)
      // CARGO-8075 【海运出口-海运订舱极速版】费用页签，第二次选择费用名称，未自动变更币种
      charge.currency = c.defaultarcurrency || localCurrency
      if (!c.defaultarcurrency) {
        const company = await Company.getCompanyById(charge.accountId)
        if (company && (company.designationAgent || company.overseaAgent)) charge.currency = 'USD'
      }
      // CARGO-31326 在开启IsCommittedAllowAddCost后，操作进行新增/修改/删除改单界面新增日期类型选项
      if (charge.dateType) {
        const dateType = charge.dateType
        const exchangeDate = this.getExchangeDateByDateType(job, charge.auditDate, dateType)
        charge.exchangeRate = await RateUtil.getForeignExchangeRateSafely(localCurrency, charge.currency, exchangeDate, charge.branchId)
      } else {
        charge.exchangeRate = await RateUtil.getForeignExchangeRateByJob(localCurrency, charge.currency, job, charge.branchId, function(rate) {
          charge.exchangeRate = rate
        })
      }
      charge.exchangeRate = NumberUtils.roundHalfUp(charge.exchangeRate, 8)// CARGO-7779
      if (c.measure) {
        charge.measure = c.measure
      }
      const item = {}
      item.receivable = charge.receivable
      item.measure = charge.measure
      item.quantity = charge.quantity
      item.quantityStr = ''
      await this.refreshQuantity(item, job)
      charge.quantity = item.quantity
      charge.serviceType = c.serviceType
      // CARGO-13028 ID：7487 全是威 费用录入费用名后结算对象与开票抬头消失不会显示
      // charge.accountId = 0
      // charge.invoiceTitle = ''
      if (!accountObjectControlMode) {
        if (c.defaultArAccountId > 0 && c.defaultArCode && c.useDefaultArCode) {
          charge.accountId = c.defaultArAccountId
        } else if (c.defaultArAccountId <= 0 && c.defaultArCode && c.useDefaultArCode) {
          charge.accountId = 0
          charge.invoiceTitle = ''
        } else if (c.defaultArAccountId > 0 && !c.defaultArCode && !c.useDefaultArCode) {
          charge.accountId = c.defaultArAccountId
        } else if (c.defaultArAccountId > 0 && c.defaultArCode && !c.useDefaultArCode) {
          charge.accountId = c.defaultArAccountId
        } else if (c.defaultArAccountId <= 0 && c.defaultArCode && !c.useDefaultArCode) {
          const accountId = ChargeUtils.getChargeDefaultChargeItem(job, c.defaultArCode)
          if (accountId > 0) {
            const company = getObjectByCache(TYPE_NAMES.basic_data_Company, 'cmpIdx', accountId)
            if (!company.ifCustomer) { // 应收，非客户时，清空结算对象
              charge.accountId = 0
            }
            charge.accountId = accountId
            // if (taxRateModifyMode === 1) {
            //   await ChargeUtils.getChargeisOverseaNotControlledByConfigIgnoreTaxRate(charge)
            // } else {
            //   await ChargeUtils.getChargeisOverseaNotControlledByConfig(charge)
            // }
          }
        } else {
          if (c.defaultArCode && c.defaultArCode !== 'QXZ') {
            const accountId = ChargeUtils.getChargeDefaultChargeItem(job, c.defaultArCode)
            if (accountId > 0) {
              const company = getObjectByCache(TYPE_NAMES.basic_data_Company, 'cmpIdx', accountId)
              if (!company.ifCustomer) { // 应收，非客户时，清空结算对象
                charge.accountId = 0
              }
              charge.accountId = accountId
              // if (taxRateModifyMode === 1) {
              //   await ChargeUtils.getChargeisOverseaNotControlledByConfigIgnoreTaxRate(charge)
              // } else {
              //   await ChargeUtils.getChargeisOverseaNotControlledByConfig(charge)
              // }
            }
          } else {
          // charge.accountId = 0
          // charge.invoiceTitle = ''
          }
        }
        if (charge.accountId > 0) {
          const company = getObjectByCache(TYPE_NAMES.basic_data_Company, 'cmpIdx', charge.accountId)
          if (!company.ifCustomer) { // 应收，非客户时，清空结算对象
            charge.accountId = 0
          }
        }
        if (charge.accountId > 0) {
          if (charge.accountId !== oldAccountId) {
            if (taxRateModifyMode === 1) {
              await ChargeUtils.getChargeisOverseaNotControlledByConfigIgnoreTaxRate(charge)
            } else {
              await ChargeUtils.getChargeisOverseaNotControlledByConfig(charge)
            }
          }
          await this.getInvoiceTitle(charge)
          // 结算代理与结算对象保持一致
          await this.getSettlementByCompanyId(charge, currentViewCacheData)
        }
      }
    }
  }

  /**
   * 费用项选择(应付)
   */
  static async changeApCl(c, charge, job, currentViewCacheData) {
    if (!c) {
      return
    }
    const oldAccountId = charge.accountId
    const localCurrency = RateUtil.localCurrencyByOfficeId(charge.branchId)
    // CARGO-8075 【海运出口-海运订舱极速版】费用页签，第二次选择费用名称，未自动变更币种
    charge.currency = c.defaultapcurrency || localCurrency
    if (!c.defaultapcurrency) {
      const company = await Company.getCompanyById(charge.accountId)
      if (company && (company.designationAgent || company.overseaAgent)) charge.currency = 'USD'
    }
    // const company = await Company.getCompanyById(charge.accountId)
    // if (!(company && (company.designationAgent || company.overseaAgent))) {
    //   if (c.defaultapcurrency != null && c.defaultapcurrency !== '') {
    //     charge.currency = c.defaultapcurrency
    //   } else {
    //     charge.currency = 'RMB'
    //   }
    // }
    // CARGO-31326 在开启IsCommittedAllowAddCost后，操作进行新增/修改/删除改单界面新增日期类型选项
    if (charge.dateType) {
      const dateType = charge.dateType
      const exchangeDate = this.getExchangeDateByDateType(job, charge.auditDate, dateType)
      charge.exchangeRate = await RateUtil.getForeignExchangeRateSafely(localCurrency, charge.currency, exchangeDate, charge.branchId)
    } else {
      charge.exchangeRate = await RateUtil.getForeignExchangeRateByJob(localCurrency, charge.currency, job, charge.branchId, function(rate) {
        charge.exchangeRate = rate
      })
    }
    charge.exchangeRate = NumberUtils.roundHalfUp(charge.exchangeRate, 8)// CARGO-7779
    if (c.measure) {
      charge.measure = c.measure
    }
    const item = {}
    item.receivable = charge.receivable
    item.measure = charge.measure
    item.quantity = charge.quantity
    item.quantityStr = ''
    await this.refreshQuantity(item, job)
    charge.quantity = item.quantity
    charge.serviceType = c.serviceType
    if (c.defaultApAccountId > 0 && c.defaultApCode && c.useDefaultApCode) {
      charge.accountId = c.defaultApAccountId
    } else if (c.defaultApAccountId <= 0 && c.defaultApCode && c.useDefaultApCode) {
      charge.accountId = 0
      charge.invoiceTitle = ''
    } else if (c.defaultApAccountId > 0 && !c.defaultApCode && !c.useDefaultApCode) {
      charge.accountId = c.defaultApAccountId
    } else if (c.defaultApAccountId > 0 && c.defaultApCode && !c.useDefaultApCode) {
      charge.accountId = c.defaultApAccountId
    } else if (c.defaultApAccountId <= 0 && c.defaultApCode && !c.useDefaultApCode) {
      const accountId = ChargeUtils.getChargeDefaultChargeItem(job, c.defaultApCode)
      if (accountId > 0) {
        const company = getObjectByCache(TYPE_NAMES.basic_data_Company, 'cmpIdx', accountId)
        if (!company.ifSupplier) { // 应付，非供应商时，清空结算对象
          charge.accountId = 0
        }
        charge.accountId = accountId
        // await ChargeUtils.getChargeisOverseaNotControlledByConfig(charge)
      }
    } else {
      if (c.defaultApCode && c.defaultApCode !== 'QXZ') {
        const accountId = ChargeUtils.getChargeDefaultChargeItem(job, c.defaultApCode)
        if (accountId > 0) {
          const company = getObjectByCache(TYPE_NAMES.basic_data_Company, 'cmpIdx', accountId)
          if (!company.ifSupplier) { // 应付，非供应商时，清空结算对象
            charge.accountId = 0
          }
          charge.accountId = accountId
          // await ChargeUtils.getChargeisOverseaNotControlledByConfig(charge)
        }
      } else {
        // charge.accountId = 0
        // charge.invoiceTitle = ''
      }
    }
    if (charge.accountId > 0) {
      const company = getObjectByCache(TYPE_NAMES.basic_data_Company, 'cmpIdx', charge.accountId)
      if (!company.ifSupplier) { // 应付，非供应商时，清空结算对象
        charge.accountId = 0
      }
    }
    if (charge.accountId > 0) {
      if (charge.accountId !== oldAccountId) {
        await ChargeUtils.getChargeisOverseaNotControlledByConfig(charge)
      }
      await this.getInvoiceTitle(charge)
      // 结算代理与结算对象保持一致
      await this.getSettlementByCompanyId(charge, currentViewCacheData)
    }
  }

  // 获取开票抬头【单票费用】
  static async getInvoiceTitle(copyedCharge) {
    if (!copyedCharge) return
    await ChargeUtils.updateDefaultInvoiceTitle([copyedCharge])
  }

  // 根据结算对象，获取结算代理
  static async getSettlementByCompanyId(charge, currentViewCacheData) {
    const accountId = charge.accountId
    if (!accountId || accountId <= 0) return
    const chargeAccount = getObjectByCache(TYPE_NAMES.basic_data_Company, 'cmpIdx', charge.accountId)
    if (!chargeAccount) {
      return
    }
    if (currentViewCacheData.customerSettlementAgentMap.has(accountId)) {
      const staticOptions = currentViewCacheData.customerSettlementAgentMap.get(accountId)
      const index = staticOptions.findIndex(p => p.uuid === chargeAccount.uuid)
      if (index < 0) {
        if (chargeAccount) {
          staticOptions.push(chargeAccount)
        }
        currentViewCacheData.customerSettlementAgentMap.set(accountId, staticOptions)
      }
      charge.settlementAgentId = staticOptions[0].cmpIdx
    } else {
      const res = await getSettlementAgentsByCmpIdx(charge.accountId)
      let dbSettlementAgents = res.data
      if (!dbSettlementAgents) dbSettlementAgents = []
      if (chargeAccount) {
        const index = dbSettlementAgents.findIndex(p => p.uuid === chargeAccount.uuid)
        if (index < 0) {
          dbSettlementAgents.push(chargeAccount)
        }
      }
      charge.settlementAgentId = dbSettlementAgents[0].cmpIdx
      currentViewCacheData.customerSettlementAgentMap.set(accountId, dbSettlementAgents)
    }
  }

  static async getSpecifyBoxTypeQuantity(size, type, job) {
    let n = 0
    if (!job || !job.seaEQ) {
      await new Job(job).getSeaEQ()
    }
    if (!job.seaEQ) {
      return n
    }
    for (const eq of job.seaEQ) {
      // 箱型 箱种都配置情况
      if (size && type && eq.eqSize === size && eq.eqType === type) {
        n = n + eq.eqQuantity
      }
      if (size && !type && eq.eqSize === size) {
        n = n + eq.eqQuantity
      }
    }
    return n
  }

  static queryMeasureData(value) {
    const l = getCacheListByKey(basicCacheKeys.basic_data_ChargeUnit)
    for (const item of l) {
      if (item && item.code === value) {
        return item
      }
    }
    return ''
  }

  static async refreshQuantity(item, job) {
    await cache.load(cacheName.CONFIG)
    const cacheConfig = cache.config
    const measure = item.measure
    let quantity = item.quantity
    const quantityStr = item.quantityStr

    const measureData = this.queryMeasureData(measure)
    if (measureData.specifyBoxType && measureData.eqSize) {
      quantity = await this.getSpecifyBoxTypeQuantity(measureData.eqSize, measureData.eqType, job)
    } else {
      if (measure === ChargeMeasure.WGT_CHARGE_MEASURE.code) {
        const weight = ChargeUtils.getChargeWeightValueType(true) === 0 ? job.weightActual : job.weightContract
        if (String(cache.config.get('ToExoSystem')) === 'XYCW' && job.lob === 'SE' && BillType.MERGE_BILL_TYPE.code === job.typeHBL && job.seaCargoDoc) {
          const hbl2 = job.seaCargoDoc.find(p => p.docType === CargoDocConsts.HBL2)
          if (hbl2 && hbl2.weight) {
            quantity = hbl2.weight
          }
        } else if (item.relatedMbl && job.cargoDoc) {
          const doc = job.cargoDoc.find(p => p.chargeRelatedBLNo === item.relatedMbl)
          if (doc && doc.weight) {
            quantity = doc.weight
          }
        } else if (job instanceof AirImportJob || job instanceof SeaImportJob || ['AI', 'SI'].includes(job.lob)) {
          quantity = job.weightContract
        } else {
          quantity = weight
        }
      } else if (measure === ChargeMeasure.WGTT_CHARGE_MEASURE.code) {
        if (item.relatedMbl && job.cargoDoc) {
          const doc = job.cargoDoc.find(p => p.chargeRelatedBLNo === item.relatedMbl)
          if (doc && doc.weight) {
            quantity = doc.weight / 1000
          }
        } else if (job.lob === 'SE' && BillType.MERGE_BILL_TYPE.code === job.typeHBL && job.seaCargoDoc) {
          const hbl2 = job.seaCargoDoc.find(p => p.docType === CargoDocConsts.HBL2)
          if (hbl2 && hbl2.weight) {
            quantity = hbl2.weight / 1000
          }
        } else if (job instanceof AirImportJob || job instanceof SeaImportJob || ['AI', 'SI'].includes(job.lob)) {
          quantity = job.weightContract / 1000
        } else {
          const weight = ChargeUtils.getChargeWeightValueType(true) === 0 ? job.weightActual : job.weightContract
          quantity = weight / 1000
        }
      } else if (measure === ChargeMeasure.WGTX_CHARGE_MEASURE.code) {
        if (String(cache.config.get('ToExoSystem')) === 'XYCW' && job.lob === 'SE' && BillType.MERGE_BILL_TYPE.code === job.typeHBL && job.seaCargoDoc) {
          const hbl2 = job.seaCargoDoc.find(p => p.docType === CargoDocConsts.HBL2)
          if (hbl2 && hbl2.chargeWeight) {
            quantity = hbl2.chargeWeight
          }
        } else if (item.relatedMbl && job.cargoDoc) {
          const doc = job.cargoDoc.find(p => p.chargeRelatedBLNo === item.relatedMbl)
          if (doc && doc.chargeWeight) {
            quantity = doc.chargeWeight
          }
        } else if (job instanceof AirExportJob || job.lob === 'AE') {
          const air = (job)
          // 应收取分单的计费重量,应付取制单的计费重量
          const getHBLcWeight = parseInt(cacheConfig.get('GetHBLcWeight')) === 1
          if (getHBLcWeight) {
            quantity = await ChargeUtils.getMHLcWeight(item.receivable, job)
          } else {
            quantity = air.cWeight
          }
        } else {
          quantity = job.chargeWeight
        }
      } else if (measure === 'PIECE') {
        if (job instanceof AirExportJob || job.lob === 'AE') {
          quantity = (job).quantityActual
        } else if (job instanceof AirImportJob || job.lob === 'AI') {
          quantity = (job).quantityContract
        } else if (job instanceof SeaExportJob || job.lob === 'SE') {
          quantity = (job).quantityActual
        } else if (job instanceof SeaImportJob || job.lob === 'SI') {
          quantity = (job).quantityContract
        } else if (job instanceof RailwayExportJob || (job.lob === 'RE' || job.lob === 'RI')) {
          quantity = (job).quantityActual
        }
      } else if (measure === ChargeMeasure.INSIDEPIECE_CHARGE_MEASURE.code) {
        if (job.lob === 'SE') {
          if (String(cache.config.get('ToExoSystem')) === 'XYCW' && BillType.MERGE_BILL_TYPE.code === job.typeHBL && job.seaCargoDoc) {
            const hbl2 = job.seaCargoDoc.find(p => p.docType === CargoDocConsts.HBL2)
            if (hbl2 && hbl2.insideQuantity) {
              quantity = hbl2.insideQuantity
            }
          } else {
            quantity = job.insideQuantity
          }
        }
      } else if (measure === ChargeMeasure.VOL_CHARGE_MEASURE.code) {
        if (String(cache.config.get('ToExoSystem')) === 'XYCW' && job.lob === 'SE' && BillType.MERGE_BILL_TYPE.code === job.typeHBL && job.seaCargoDoc) {
          const hbl2 = job.seaCargoDoc.find(p => p.docType === CargoDocConsts.HBL2)
          if (hbl2 && hbl2.cubage) {
            quantity = hbl2.cubage
          }
        } else if (item.relatedMbl && job.cargoDoc) {
          const doc = job.cargoDoc.find(p => p.chargeRelatedBLNo === item.relatedMbl)
          if (doc && doc.cubage) {
            quantity = doc.cubage
          }
        } else if (job instanceof SeaImportJob || job.lob === 'SI') {
          quantity = job.cubageContract
        } else {
          quantity = ChargeUtils.getVOLQuantity(job)
        }
      } else if (measure === 'MIN') {
        quantity = 1
      } else if (measure === 'BOX') {
        quantity = 1
      } else if (measure === 'NML') {
        quantity = 1
      } else if (measure === 'CNTR') {
        quantity = 0
        if (!job || !job.seaEQ) {
          await new Job(job).getSeaEQ()
        }
        if (job.seaEQ) {
          for (const seq of job.seaEQ) {
            quantity += seq.eqQuantity
          }
        }
      } else if (measure === 'BL') {
        quantity = 1
      } else if (measure === 'SHIPMENT') {
        quantity = 1
      } else {
        quantity = await this.getEQQuantity(measure, job)
      }
    }
    if (measure === 'vehicle' || measure === 'day') {
      quantity = 0
    } else if (quantity < 1) {
      quantity = 1
    }
    quantity = await ChargeUtils.getQuantityByChargeWeightValueType2(job, quantity, measure)
    item.measure = measure
    item.quantity = quantity.toFixed(3)
    item.quantityStr = quantityStr
  }

  /**
   * 结算对象选择
   * @param cost
   * @param params
   * @param isRowEdit
   * @param job
   * @param view
   * @param currentViewCacheData
   * @param taxRateModifyMode 税率修改方式（0：默认 1：录入费用名称，若费用名称税率不为0的，更新此条费用的税率，否则不更新）
   * @param currencyChangeBySettlementAccount 币种修改方式（2：默认逻辑 0=修改结算对象不影响币种，1=仅首次填写结算对象时修改币种；
   */
  static async accountIdChange(cost, params, isRowEdit, job, view, currentViewCacheData, taxRateModifyMode = 0, currencyChangeBySettlementAccount = 2) {
    if (isRowEdit) {
      await cache.load(cacheName.CONFIG)
      const cacheConfig = cache.config
      const canChangeOverseaOfCharge = parseInt(cacheConfig.get('canChangeOverseaOfCharge'))
      const chargeAuto = getObjectByCache(TYPE_NAMES.basic_data_Company, 'cmpIdx', params.value)
      if (chargeAuto && cost && this.changeAccountCheck(params.originalValue, cost, canChangeOverseaOfCharge) && cost.accountId !== params.originalValue) {
        cost.accountId = chargeAuto.cmpIdx
        await ChargeUtils.getChargeisOverseaNotControlledByConfigIgnoreTaxRate(cost)
        if (taxRateModifyMode === 1) {
          ChargeUtils.changeChargeRateByAccountId(cost)
        } else {
          ChargeUtils.changeItemRate(cost)
        }
        const c = await Company.getCompanyById(cost.accountId)
        if (c) {
          cost.oversea = (c.designationAgent || c.overseaAgent) // 指定货代理/目的港代理
          if (canChangeOverseaOfCharge === 2) {
            cost.oversea = false
          } else if (canChangeOverseaOfCharge === 3) {
            cost.oversea = true
          }
          const localCurrency = RateUtil.localCurrencyByOfficeId(cost.branchId)
          if (currencyChangeBySettlementAccount === 2 || (currencyChangeBySettlementAccount === 1 && params.firstAccountId)) {
            if (c.designationAgent) {
              cost.currency = 'USD'
            } else {
              const cItem = ChargeItem.getChargeItemById(cost.chargeItemId)
              if (cItem) {
                if (cost.receivable) {
                  cost.currency = !cItem.defaultarcurrency ? localCurrency : cItem.defaultarcurrency
                } else {
                  cost.currency = !cItem.defaultapcurrency ? localCurrency : cItem.defaultapcurrency
                }
              }
            }
          }
          // CARGO-31326 在开启IsCommittedAllowAddCost后，操作进行新增/修改/删除改单界面新增日期类型选项
          if (cost.dateType) {
            const dateType = cost.dateType
            const exchangeDate = this.getExchangeDateByDateType(job, cost.auditDate, dateType)
            cost.exchangeRate = await RateUtil.getForeignExchangeRateSafely(localCurrency, cost.currency, exchangeDate, cost.branchId)
          } else {
            cost.exchangeRate = await RateUtil.getForeignExchangeRateByJob(localCurrency, cost.currency, job, cost.branchId, function(rate) {
              cost.exchangeRate = rate
            })
          }
          // CARGO-4980 单价是否含税
          if (cost.unitPriceIncludeTax) {
            ChargeUtils.calculateTaxAmount(cost, cost.amount, cost.taxRate)
          } else {
            ChargeUtils.calculateTax(cost, cost.excludingTaxAmount, cost.taxRate)
          }
          if (cost.accountId > 0) {
            await this.getInvoiceTitle(cost)
          }
          if (view) {
            view.companyTitle = c
            view.costCharge = cost
          }
        }
        await this.getSettlementByCompanyId(cost, currentViewCacheData)
      }
    }
  }

  /**
   * @private
   */
  static changeAccountCheck(selectedId, cost, canChangeOverseaOfCharge) {
    if (canChangeOverseaOfCharge === 0) {
      return true
    }
    if (selectedId === cost.accountId) {
      return false
    }
    return true
  }

  /**
   * 单位选择(应收,应付)
   */
  static async changeChargeMeasureCombo(charge, params, job) {
    if (params && params.value && params.value.length > 0) {
      charge.measure = params.value
      await this.refreshQuantity(charge, job)
      // CARGO-4980 单价是否含税
      if (charge.unitPriceIncludeTax) {
        ChargeUtils.calculateTaxAmount(charge, multiply(charge.quantity, charge.unitRate), charge.taxRate)
      } else {
        ChargeUtils.calculateTax(charge, multiply(charge.quantity, charge.unitRate), charge.taxRate)
      }
    }
  }

  /**
   * 切换关联提单号时需要触发重新计算(应收,应付)
   */
  static async changeChargeRelatedMblCombo(charge, params, job) {
    await this.refreshQuantity(charge, job)
    // CARGO-4980 单价是否含税
    if (charge.unitPriceIncludeTax) {
      ChargeUtils.calculateTaxAmount(charge, multiply(charge.quantity, charge.unitRate), charge.taxRate)
    } else {
      ChargeUtils.calculateTax(charge, multiply(charge.quantity, charge.unitRate), charge.taxRate)
    }
  }
  /**
   * 币种选择(应收,应付)
   */
  static async currencyChangeHandler(charge, job) {
    const localCurrency = RateUtil.localCurrencyByOfficeId(charge.branchId)
    // CARGO-31326 在开启IsCommittedAllowAddCost后，操作进行新增/修改/删除改单界面新增日期类型选项
    if (charge.dateType) {
      const dateType = charge.dateType
      const exchangeDate = this.getExchangeDateByDateType(job, charge.auditDate, dateType)
      charge.exchangeRate = await RateUtil.getForeignExchangeRateSafely(localCurrency, charge.currency, exchangeDate, charge.branchId)
    } else {
      charge.exchangeRate = await RateUtil.getForeignExchangeRateByJob(localCurrency, charge.currency, job, charge.branchId)
    }
    charge.exchangeRate = NumberUtils.roundHalfUp(charge.exchangeRate, 8) // CARGO-7779
    // CARGO-29281，切换币种时，含税价和不含税价的数据没有重新计算
    if (!isNaN(charge.quantity) && !isNaN(charge.unitRate) && !isNaN(charge.taxRate) && RateUtil.useAmountCarryRule()) {
      if (charge.unitPriceIncludeTax) {
        ChargeUtils.calculateTaxAmount(charge, multiply(charge.quantity, charge.unitRate), charge.taxRate)
      } else {
        ChargeUtils.calculateTax(charge, multiply(charge.quantity, charge.unitRate), charge.taxRate)
      }
    }
  }

  static async dateTypeChangeHandler(charge, job) {
    const dateType = charge.dateType
    const exchangeDate = this.getExchangeDateByDateType(job, charge.auditDate, dateType)
    const localCurrency = RateUtil.localCurrencyByOfficeId(charge.branchId)
    const exchangeRate = await RateUtil.getForeignExchangeRateSafely(localCurrency, charge.currency, exchangeDate, charge.branchId)
    charge.exchangeRate = isNaN(exchangeRate) ? '1' : exchangeRate.toString()
    if (!isNaN(charge.quantity) && !isNaN(charge.unitRate) && !isNaN(charge.taxRate) && RateUtil.useAmountCarryRule()) {
      if (charge.unitPriceIncludeTax) {
        ChargeUtils.calculateTaxAmount(charge, multiply(charge.quantity, charge.unitRate), charge.taxRate)
      } else {
        ChargeUtils.calculateTax(charge, multiply(charge.quantity, charge.unitRate), charge.taxRate)
      }
    }
  }

  // 通过日期类型获取汇率对应日期
  static getExchangeDateByDateType(job, adjustmentDate, dateType) {
    if (dateType === 'AdjustmentDate') {
      return adjustmentDate
    } else if (dateType === 'BusinessDate') {
      return ['SE', 'AE', 'RE'].includes(job.lob) ? job.etd : job.eta
    } else {
      throw new Error('getExchangeDateByDateType: Not processed!')
    }
  }

  /**
   * 数量填写
   */
  static quantityChange(cost, params) {
    const quantity = params.value
    let q = NumberUtils.parseNumber(quantity, 3)
    if (!isNaN(q)) {
      q = NumberUtils.roundHalfUp(q, 3)
      // todo-shengsheng  Desc: 原始值和新值对比，此处无意义，因为 触发change,肯定是值有所变化了
      if (params.originalValue !== q) {
        if (cost.unitPriceIncludeTax) {
          ChargeUtils.calculateTaxAmount(cost, multiply(q, cost.unitRate), cost.taxRate)
        } else {
          ChargeUtils.calculateTax(cost, multiply(q, cost.unitRate), cost.taxRate)
        }
      }
    }
  }

  /**
   * 单价填写
   */
  static unitRateChange(cost, params) {
    const unit = params.value
    let u = NumberUtils.parseNumber(unit, 3)
    if (!isNaN(u) && params.originalValue !== u) {
      u = NumberUtils.roundHalfUp(u, 3)
      if (cost.unitPriceIncludeTax) {
        ChargeUtils.calculateTaxAmount(cost, multiply(u, cost.quantity), cost.taxRate)
      } else {
        ChargeUtils.calculateTax(cost, multiply(u, cost.quantity), cost.taxRate)
      }
    }
  }

  /**
   * 含税价填写
   */
  static amountChange(cost, params) {
    const newData = params.value
    const newAmount = parseFloat(newData)
    if (!isNaN(newAmount) && parseFloat(params.originalValue) !== newAmount) {
      ChargeUtils.calculateTaxAmount(cost, newAmount, Number(cost.taxRate))
    }
  }

  /**
   * 不含税价填写
   */
  static excludingTaxAmountChange(cost, params) {
    const excludingTaxAmount = params.value
    const ex = parseFloat(excludingTaxAmount)
    if (!isNaN(ex) && parseFloat(params.originalValue) !== ex) {
      ChargeUtils.calculateTax(cost, ex, Number(cost.taxRate), true)
    }
  }

  /**
   * 税率
   */
  static taxRateChange(cost, params) {
    const txtTaxRate = params.value
    let taxRate = parseFloat(txtTaxRate)
    if (isNaN(taxRate)) {
      taxRate = 0.0
    }
    if (params.originalValue !== taxRate) {
      if (cost.unitPriceIncludeTax) {
        ChargeUtils.calculateTaxAmount(cost, Number(cost.amount), taxRate)
      } else {
        ChargeUtils.calculateTax(cost, Number(cost.excludingTaxAmount), taxRate)
      }
    }
  }

  /**
   * 修改单价是否含税
   */
  static async unitPriceIncludeTaxChange(cost) {
    console.info('unitPriceIncludeTax:' + cost.unitPriceIncludeTax)
    if (cost.unitPriceIncludeTax) {
      ChargeUtils.calculateTaxAmount(cost, cost.unitRate * cost.quantity, cost.taxRate)
    } else {
      ChargeUtils.calculateTax(cost, cost.unitRate * cost.quantity, cost.taxRate)
    }
  }

  /**
   *  加载结算对象联系人
   * @param accountId
   * @returns {Promise<unknown>}
   */
  static async getContactsByCompanyId(accountId, currentViewCacheData) {
    if (!accountId || accountId <= 0) return
    if (currentViewCacheData.companyContactPersonMap.has(accountId)) {
      return currentViewCacheData.companyContactPersonMap.get(accountId)
    }
    const res = await getContacts({ cmpIdx: accountId })
    currentViewCacheData.companyContactPersonMap.set(accountId, res.data)

    return currentViewCacheData.companyContactPersonMap.get(accountId)
  }

  /**
   * 更改费用编辑处理
   * @param params 表格参数
   * @param job 业务
   * @param currentViewCacheData 缓存对象
   * @param inputIncludingTaxUnitPrice 单价是否含税（参数）
   * @returns {Promise<void>}
   */
  static async changeChargeCellChangeHandler(params, job, currentViewCacheData, inputIncludingTaxUnitPrice, currencyChangeBySettlementAccount = 2) {
    const charge = params.data
    const field = params.field
    // if (charge.receivable && String(charge.taxRate) === 'NaN') {
    //   const office = $store.getters.currentOffice
    //   if (office && String(office.taxRate) !== 'NaN') {
    //     charge.taxRate = office.taxRate
    //   }
    // }
    // debugger
    switch (field) {
      case 'chargeItemId': {
        await ChargeColumnUtil.chargeItemChange(charge, params, true, job, currentViewCacheData, 1)
        break
      }
      case 'accountId': {
        await ChargeColumnUtil.accountIdChange(charge, params, true, job, null, currentViewCacheData, 1, currencyChangeBySettlementAccount)
        break
      }
      case 'measure': {
        await ChargeColumnUtil.changeChargeMeasureCombo(charge, params, job)
        break
      }
      case 'currency': {
        await ChargeColumnUtil.currencyChangeHandler(charge, job)
        break
      }
      case 'dateType': {
        await ChargeColumnUtil.dateTypeChangeHandler(charge, job)
        break
      }
      case 'quantity': {
        await ChargeColumnUtil.quantityChange(charge, params)
        break
      }
      case 'unitRate': {
        await ChargeColumnUtil.unitRateChange(charge, params)
        break
      }
      case 'amount': {
        await ChargeColumnUtil.amountChange(charge, params)
        break
      }
      case 'excludingTaxAmount': {
        await ChargeColumnUtil.excludingTaxAmountChange(charge, params)
        break
      }
      case 'taxRate': {
        await ChargeColumnUtil.taxRateChange(charge, params)
        break
      }
      case 'unitPriceIncludeTax': {
        await ChargeColumnUtil.unitPriceIncludeTaxChange(charge)
        break
      }
      default: {
        break
      }
    }
    if (isNaN(charge.unitRate) || charge.unitRate === 0) {
      charge.unitRate = Number((charge.excludingTaxAmount / charge.quantity).toFixed(3))
      if (inputIncludingTaxUnitPrice) {
        charge.unitRate = Number((charge.taxAmount / charge.quantity).toFixed(3))
      }
    }
    await this.getContactsByCompanyId(charge.accountId, currentViewCacheData)
  }
}
